import {
  Timestamp,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
  deleteField,
  FieldValue,
} from '@angular/fire/firestore';
import { FieldOp, isFieldOp } from './tenant-collection';

/** Convert a seam FieldOp marker to the real Firestore FieldValue. */
function fieldOpToFirestore(o: FieldOp): FieldValue {
  switch (o.__op) {
    case 'increment':
      return increment(o.n);
    case 'arrayUnion':
      return arrayUnion(...o.values);
    case 'arrayRemove':
      return arrayRemove(...o.values);
    case 'serverTimestamp':
      return serverTimestamp();
    case 'delete':
      return deleteField();
  }
}

/**
 * The Timestamp <-> Date codec for the TenantCollection seam.
 *
 * Read:  recursively convert any Firestore Timestamp to a Date. Verified safe —
 *        every date-typed field in the models is a `Date`; nothing is meant to
 *        stay a Timestamp, and non-Timestamps (e.g. a unix `number`) lack
 *        `.toDate()` so they pass through untouched.
 * Write: recursively convert any `Date` to a Firestore Timestamp. Audit fields
 *        (`createdAt`/`updatedAt`) are set by the seam as `serverTimestamp()`,
 *        never by hand.
 */

function isTimestampLike(v: unknown): v is { toDate: () => Date } {
  return (
    typeof v === 'object' && v !== null && typeof (v as { toDate?: unknown }).toDate === 'function'
  );
}

/** Recursively map stored Firestore values to model values (Timestamp -> Date). */
export function fromFirestore<T>(raw: Record<string, unknown>, id: string): T {
  return { id, ...(decode(raw) as Record<string, unknown>) } as T;
}

function decode(value: unknown): unknown {
  if (isTimestampLike(value)) return value.toDate();
  if (Array.isArray(value)) return value.map(decode);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = decode(v);
    }
    return out;
  }
  return value;
}

/** Recursively map model values to Firestore values (Date -> Timestamp, FieldOp -> FieldValue). */
export function encode(value: unknown): unknown {
  if (value instanceof Date) return Timestamp.fromDate(value);
  if (isFieldOp(value)) return fieldOpToFirestore(value);
  if (Array.isArray(value)) return value.map(encode);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue; // Firestore rejects undefined (see CLAUDE.md)
      out[k] = encode(v);
    }
    return out;
  }
  return value;
}

/** Encode a write payload and stamp audit fields. */
export function encodeForWrite(
  data: Record<string, unknown>,
  mode: 'create' | 'update'
): Record<string, unknown> {
  const out = encode(data) as Record<string, unknown>;
  out['updatedAt'] = serverTimestamp() as FieldValue;
  if (mode === 'create') out['createdAt'] = serverTimestamp() as FieldValue;
  return out;
}

/** Coerce a query argument so a `Date` filter compares against a stored Timestamp. */
export function encodeQueryValue(value: unknown): unknown {
  if (value instanceof Date) return Timestamp.fromDate(value);
  if (Array.isArray(value)) return value.map(encodeQueryValue);
  return value;
}
